/**
 * NextAuth セッションから `AuthenticatedUser` を組み立てる（Issue #8078 で切り出し）。
 *
 * ## いつ使うのか
 *
 * 初回ロードで `/api/auth/me` が引けない、または**受け入れられる形で返ってこない**ときの
 * フォールバック（Issue #8076 の検証で弾かれた場合を含む）。セッションには workspace 情報が
 * 無いので、ここで組んだ値は `workspace: undefined` のまま。
 *
 * ## なぜ切り出したのか
 *
 * `AuthProvider` の初回ロード effect が 165 行・複雑度 40 になっていて、その大半がこの
 * 組み立てだった。**純粋な写像**（セッション → ユーザー）なので、状態を触る effect から
 * 出せる。`src/components/CLAUDE.md` の 500 行基準と複雑度の両方に効く。
 *
 * ## 既定の権限が 2 種類あるのは意図
 *
 * 組織が決まっていれば `established`、決まっていなければ `onboarding`。**後者は全部 false**
 * にする ── 組織が無い状態で「作れる」と言うと、押した先で必ず失敗する。
 *
 * `established` 側は `session.user.permissions`（NextAuth の session callback が DB から
 * 計算した値）を優先し、無いときだけ既定へ倒す。
 */
import type { AuthenticatedUser, WorkspaceRole } from '@/lib/auth/types';

/**
 * 組み立てに使うセッションの断面。
 *
 * `next-auth` の `Session` 型に依存させない ── この関数は純粋な写像で、セッションの
 * 出自を知る必要がない（テストからも素の値で呼べる）。
 */
export interface SessionForFallback {
  user: {
    /**
     * **必須**（Issue #8078 レビュー指摘）。
     *
     * 空の `id` を持つ `AuthenticatedUser` を作れてしまうと、`parseAuthenticatedUser`
     * （Issue #8076）が「空でない文字列」を要求しているのと食い違う ── 同じアプリの中で
     * 受け入れ基準が 2 つある状態になる。
     *
     * 呼び出し経路では `resolveSessionAction` が `id` の無いセッションを `load` へ進めない
     * ので、実際に欠けた値は届かない。**それを型でも表す。**
     */
    id: string;
    cognitoId?: string;
    email?: string;
    name?: string;
    currentOrganizationId?: string;
    currentWorkspaceId?: string;
    isInternal?: boolean;
    permissions?: AuthenticatedUser['permissions'];
    /**
     * 現在ワークスペースの membership（`session-builder` がセッションへ入れる）。
     *
     * **`role` だけを要求する。** セッションに載っているのは DB 層の形で、auth 層の
     * `WorkspaceMembership` が持つ `permissions` を持たない（`nextauth-adapter` の NOTE と同じ
     * 構造差分）。ここで全体を要求すると、実際に渡ってくるセッションが型に合わなくなる。
     */
    workspaceMembership?: { role: WorkspaceRole };
  };
  organization?: AuthenticatedUser['organization'];
}

/**
 * 組織が決まっているときの既定権限。
 *
 * **`session.user.permissions` が無いときだけ**使う。NextAuth の session callback が DB から
 * 計算した値があるならそちらが正しい ── 既定で上書きすると管理者が管理操作を見失う。
 *
 * 「ページやフローは作れるが、組織は触れない」に寄せる。組織側を true にすると、権限が
 * 分からない状態で管理操作を見せることになる。
 */
const ESTABLISHED_DEFAULT_PERMISSIONS: AuthenticatedUser['permissions'] = {
    canManageOrganization: false,
    canInviteMembers: false,
    canManageBilling: false,
    canManageOrganizationKnowledge: false,
    canApproveOrganizationKnowledge: false,
    canCreateWorkspaces: true,
    canManageWorkspace: false,
    canCreatePages: true,
    canEditPages: true,
    canDeletePages: true,
    canSharePages: true,
    canCreateFlows: true,
    canEditFlows: true,
    canDeleteFlows: true,
    canShareFlows: true,
    canStartRecording: true,
    canComment: true
};

/**
 * 組織が決まっていないとき（onboarding）の権限。**全部 false。**
 *
 * 組織が無い状態で「作れる」と言うと、押した先で必ず失敗する。
 */
// **`as const` が要る。** `AuthenticatedUser` の onboarding 側は各権限を `false` リテラルで
// 持つので、`boolean` に広げると代入できない（型が「全部 false」を保証している）。
const ONBOARDING_PERMISSIONS = {
  canManageOrganization: false,
  canInviteMembers: false,
  canManageBilling: false,
  canManageOrganizationKnowledge: false,
  canApproveOrganizationKnowledge: false,
  canCreateWorkspaces: false,
  canManageWorkspace: false,
  canCreatePages: false,
  canEditPages: false,
  canDeletePages: false,
  canSharePages: false,
  canCreateFlows: false,
  canEditFlows: false,
  canDeleteFlows: false,
  canShareFlows: false,
  canStartRecording: false,
  canComment: false,
} as const;

/**
 * どちらの分岐でも同じ値。**`||` の連続をここへ集める** ── 各分岐に散らすと、同じ
 * フォールバックを 2 回書くことになり、片方だけ直す事故が起きる。
 */
function commonFields(session: SessionForFallback) {
  return {
    id: session.user.id,
    cognitoId: session.user.cognitoId || '',
    email: session.user.email || '',
    name: session.user.name || '',
    isInternal: session.user.isInternal === true,
    // セッションには workspace が無い。空オブジェクトを作ると「読み込めている」と誤解される。
    workspace: undefined,
    createdAt: new Date().toISOString(),
    avatarUrl: undefined,
  };
}

/**
 * セッションの権限をそのまま使ってよいか（Issue #8078 レビュー指摘）。
 *
 * **`{ canEditPages: 'false' }` のような値を通さない。** 文字列は truthy に評価されるので、
 * 表示ガードをすり抜けて「できるはずの操作が失敗する」画面になる。`/api/auth/me` 側は
 * Issue #8076 で同じ穴を塞いだが、セッションからのフォールバックは素通しのままだった。
 *
 * **落とすときは全部 false へ倒す**（`ESTABLISHED_DEFAULT_PERMISSIONS` へは戻さない）。
 * 既定には作成・編集の true が含まれるので、壊れた権限を「一部できる」に読み替えることに
 * なる。形が信用できないなら、できないと言う方が安全。
 *
 * キーの過不足も見る。欠けたキーは `undefined` になり、消費側では falsy に倒れるが、
 * **「意図した false」と「壊れて欠けた」を区別できない**まま画面が出る。
 */
function usablePermissions(
  permissions: AuthenticatedUser['permissions'] | undefined,
): AuthenticatedUser['permissions'] {
  if (!permissions || typeof permissions !== 'object') return ESTABLISHED_DEFAULT_PERMISSIONS;
  const keys = Object.keys(ESTABLISHED_DEFAULT_PERMISSIONS) as (keyof AuthenticatedUser['permissions'])[];
  const usable = keys.every((key) => typeof permissions[key] === 'boolean');
  return usable ? permissions : ONBOARDING_PERMISSIONS;
}

/** 組織が決まっているとき。組織情報が無ければ既定で埋める（消費側が `name` を素で読む）。 */
function buildEstablished(session: SessionForFallback): AuthenticatedUser {
  const organizationId = session.user.currentOrganizationId || '';
  return {
    ...commonFields(session),
    status: 'established' as const,
    currentOrganizationId: organizationId,
    currentWorkspaceId: session.user.currentWorkspaceId || undefined,
    organization: session.organization || {
      id: '',
      name: 'Default Organization',
      slug: 'default',
      settings: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    organizationMembership: {
      id: '',
      userId: session.user.id,
      organizationId,
      role: 'member' as const,
      joinedAt: new Date().toISOString(),
      invitedBy: undefined,
      status: 'active' as const,
    },
    /**
     * **セッションの membership を落とさない**（Issue #8187 / PR #8202 レビュー指摘）。
     *
     * ここを省くと `workspaceMembership` が `undefined` になり、role を見る側
     * （`useCanEditProject` / `MembershipBadge`）が「ロール未確定」に倒れる。閲覧専用判定の
     * 既定は「編集可」なので、**`/api/auth/me` が引けないときだけ viewer に編集の導線が出る**。
     *
     * 受け皿としてセッションの値をそのまま写す。`permissions` を持たない構造差分は
     * `nextauth-adapter` の `/api/auth/me` 経路と同じで、消費側が読むのは `role` だけ。
     */
    workspaceMembership: session.user.workspaceMembership as AuthenticatedUser['workspaceMembership'],
    // NextAuth の session callback が DB から計算した値があればそちらを使う。
    // **ただし形を確かめる**（レビュー指摘）。詳細は `usablePermissions`。
    permissions: usablePermissions(session.user.permissions),
  };
}

/** 組織が決まっていないとき。組織まわりは全部 `undefined`、権限は全部 false。 */
function buildOnboarding(session: SessionForFallback): AuthenticatedUser {
  return {
    ...commonFields(session),
    status: 'onboarding' as const,
    currentOrganizationId: undefined,
    currentWorkspaceId: undefined,
    organization: undefined,
    organizationMembership: undefined,
    permissions: ONBOARDING_PERMISSIONS,
  };
}

export function buildUserFromSession(session: SessionForFallback): AuthenticatedUser {
  return session.user.currentOrganizationId ? buildEstablished(session) : buildOnboarding(session);
}
